import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner, Stars, EmptyState } from "@/components/Common";
import { Send, Plus, CheckSquare, Square, Zap, Save, Bot, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Compose() {
  const [leads, setLeads] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [cvId, setCvId] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualHotel, setManualHotel] = useState("");
  const [manualCity, setManualCity] = useState("");
  const [sendingManual, setSendingManual] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [bot, setBot] = useState(null);
  const [botCfg, setBotCfg] = useState({ enabled: false, hour: 8, minute: 0, daily_target: 100, only_north: true });
  const [addHotel, setAddHotel] = useState("");
  const [addCity, setAddCity] = useState("");
  const [addingLead, setAddingLead] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");

  const load = async () => {
    const [l, c, s, rg] = await Promise.all([
      api.get("/leads?status=non_inviata" + (selectedRegion ? `&region=${encodeURIComponent(selectedRegion)}` : "")),
      api.get("/cv"), api.get("/settings"), api.get("/leads/regions"),
    ]);
    setLeads(l.data); setCvs(c.data); setSettings(s.data); setRegions(rg.data);
    const primary = c.data.find((x) => x.is_primary);
    setCvId(primary?.id || c.data[0]?.id || "");
    setSelected(new Set());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ===== Bot invio automatico =====
  const refreshBot = async () => {
    try {
      const r = await api.get("/bot/status");
      setBot(r.data);
      if (r.data?.config) setBotCfg(r.data.config);
    } catch { /* silenzioso */ }
  };

  const startBot = async () => {
    try {
      await api.post("/bot/start?max_emails=" + (botCfg.daily_target || 100));
      toast.success("Bot avviato");
      await refreshBot();
    } catch (e) { toast.error(e?.response?.data?.detail || "Avvio fallito"); }
  };

  const stopBot = async () => {
    try {
      await api.post("/bot/stop");
      toast.success("Stop richiesto");
      await refreshBot();
    } catch { toast.error("Stop fallito"); }
  };

  const saveBotCfg = async () => {
    try {
      await api.post("/bot/config", {
        autosend_enabled: botCfg.enabled,
        autosend_hour: botCfg.hour,
        autosend_minute: botCfg.minute,
        autosend_daily_target: botCfg.daily_target,
        autosend_only_north: botCfg.only_north,
      });
      toast.success("Impostazioni bot salvate");
      await refreshBot();
    } catch { toast.error("Salvataggio fallito"); }
  };

  useEffect(() => {
    refreshBot();
    const t = setInterval(refreshBot, 15000);
    return () => clearInterval(t);
  }, []);

  const changeRegion = async (region) => {
    setSelectedRegion(region);
    setLoading(true);
    try {
      const l = await api.get("/leads?status=non_inviata" + (region ? `&region=${encodeURIComponent(region)}` : ""));
      setLeads(l.data); setSelected(new Set());
    } finally { setLoading(false); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const saveTemplate = async () => {
    await api.put("/settings", { subject_template: settings.subject_template, message_template: settings.message_template });
    toast.success("Template salvato");
  };

  const sendBatch = async () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un hotel"); return; }
    if (cvs.length === 0) { toast.error("Carica prima un CV"); return; }
    setSending(true);
    const ids = [...selected];
    let ok = 0, fail = 0;
    for (let i = 0; i < ids.length; i++) {
      setProgress({ current: i + 1, total: ids.length });
      try {
        await api.post("/emails/send", { lead_id: ids[i], cv_id: cvId || null });
        ok++;
      } catch { fail++; }
      await new Promise((r) => setTimeout(r, 1200)); // anti-spam throttle
    }
    setSending(false);
    setProgress(null);
    setSelected(new Set());
    toast.success(`Invio batch completato`, { description: `${ok} inviate, ${fail} fallite` });
    load();
  };

  const sendManual = async () => {
    if (!manualEmail.trim()) { toast.error("Inserisci un email"); return; }
    if (cvs.length === 0) { toast.error("Carica prima un CV"); return; }
    setSendingManual(true);
    try {
      const r = await api.post("/emails/send-to", {
        to_email: manualEmail.trim(),
        hotel_name: manualHotel.trim() || null,
        city: manualCity.trim() || null,
        cv_id: cvId || null,
        subject: settings.subject_template,
        message: settings.message_template,
      });
      if (r.data.status === "simulated") {
        toast.warning("Email simulata (nessuna API key attiva)");
      } else if (r.data.status === "sent") {
        toast.success(`Email inviata a ${manualEmail.trim()}`);
      } else if (r.data.status === "failed") {
        toast.error(r.data.error || "Invio fallito");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio manuale");
    }
    setSendingManual(false);
  };

  const addLeadToList = async () => {
    if (!addEmail.trim()) { toast.error("Inserisci un email"); return; }
    setAddingLead(true);
    try {
      const r = await api.post("/leads/manual", {
        email: addEmail.trim(),
        hotel_name: addHotel.trim() || null,
        city: addCity.trim() || null,
      });
      if (r.data.exists) {
        toast.info("Email già presente nella lista");
      } else {
        toast.success(`Aggiunta ${addEmail.trim()} alla lista`);
      }
      setAddEmail(""); setAddHotel(""); setAddCity("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiunta email");
    }
    setAddingLead(false);
  };

  if (loading) return <div className="p-10 flex justify-center"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader title="Invia Candidature & Template" subtitle="Bot automatico 100 email/giorno — priorità hotel e ristoranti zona Nord" icon={Send} />

      {/* ===== BOT INVIO AUTOMATICO ===== */}
      <div className="card p-5 mb-4 border border-amber-500/20" data-testid="bot-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-head font-semibold text-gray-200 flex items-center gap-2">
            <Bot className="w-4 h-4 text-amber-400" /> Bot invio automatico
            {bot?.state?.running
              ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">IN CORSO</span>
              : <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/10">FERMO</span>}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={refreshBot} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
            </button>
            {bot?.state?.running ? (
              <button onClick={stopBot} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 flex items-center gap-1.5">
                <Square className="w-3.5 h-3.5" /> Ferma
              </button>
            ) : (
              <button onClick={startBot} className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" /> Avvia ora
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Inviate oggi</div>
            <div className="text-xl font-semibold text-amber-400">{bot?.quota?.used ?? 0} <span className="text-sm text-gray-500">/ {bot?.quota?.cap ?? 100}</span></div>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Rimanenti</div>
            <div className="text-xl font-semibold text-gray-200">{bot?.quota?.remaining ?? 0}</div>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Ciclo corrente</div>
            <div className="text-xl font-semibold text-gray-200">{bot?.state?.sent_today ?? 0} <span className="text-sm text-gray-500">inviate</span></div>
            <div className="text-[11px] text-gray-500">{bot?.state?.failed_today ?? 0} fallite</div>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Ultimo invio</div>
            <div className="text-sm font-medium text-gray-200 truncate">{bot?.state?.last_hotel || "—"}</div>
            <div className="text-[11px] text-gray-500">{bot?.state?.last_sent_at ? new Date(bot.state.last_sent_at).toLocaleTimeString("it-IT") : "nessuno"}</div>
          </div>
        </div>

        <div className="mt-3">
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
              style={{ width: `${Math.min(100, ((bot?.quota?.used ?? 0) / (bot?.quota?.cap || 100)) * 100)}%` }} />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4 mt-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Ora avvio giornaliero</label>
            <input type="time" value={`${String(botCfg.hour).padStart(2, "0")}:${String(botCfg.minute).padStart(2, "0")}`}
              onChange={(e) => { const [h, m] = e.target.value.split(":"); setBotCfg({ ...botCfg, hour: +h, minute: +m }); }}
              className="input-dark px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Email al giorno</label>
            <input type="number" min={1} max={100} value={botCfg.daily_target}
              onChange={(e) => setBotCfg({ ...botCfg, daily_target: +e.target.value })}
              className="input-dark px-3 py-2 text-sm w-24" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300 pb-2">
            <input type="checkbox" checked={!!botCfg.enabled} onChange={(e) => setBotCfg({ ...botCfg, enabled: e.target.checked })} />
            Scheduler attivo
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-300 pb-2">
            <input type="checkbox" checked={!!botCfg.only_north} onChange={(e) => setBotCfg({ ...botCfg, only_north: e.target.checked })} />
            Solo zona Nord
          </label>
          <button onClick={saveBotCfg} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-amber-500/30 text-gray-200 flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" /> Salva impostazioni
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Template editor */}
        <div className="card p-5 space-y-4 h-fit">
          <div className="flex items-center justify-between">
            <h3 className="font-head font-semibold text-gray-200">Template Messaggio</h3>
            <button data-testid="save-template-btn" onClick={saveTemplate}
              className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-amber-500/30 text-gray-200 flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> Salva
            </button>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Oggetto</label>
            <input data-testid="template-subject" value={settings.subject_template}
              onChange={(e) => setSettings({ ...settings, subject_template: e.target.value })}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Corpo del messaggio</label>
            <textarea data-testid="template-message" value={settings.message_template}
              onChange={(e) => setSettings({ ...settings, message_template: e.target.value })} rows={11}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm font-mono leading-relaxed" />
          </div>
          <div className="text-xs text-gray-500">
            Variabili: <span className="text-amber-400 font-mono">{"{NOME_HOTEL}"}</span>, <span className="text-amber-400 font-mono">{"{CITTA}"}</span>, <span className="text-amber-400 font-mono">{"{RUOLO_CANDIDATURA}"}</span>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">CV da allegare</label>
            <select data-testid="compose-cv-select" value={cvId} onChange={(e) => setCvId(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm">
              {cvs.length === 0 && <option value="">Nessun CV</option>}
              {cvs.map((c) => <option key={c.id} value={c.id}>{c.filename}{c.is_primary ? " (principale)" : ""}</option>)}
            </select>
          </div>
        </div>

        {/* Invio singolo manuale */}
        <div className="card p-5 space-y-4 h-fit">
          <h3 className="font-head font-semibold text-gray-200 flex items-center gap-2">
            <Send className="w-4 h-4 text-amber-400" /> Invio singolo manuale
          </h3>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Destinatario</label>
            <input data-testid="manual-email" type="email" placeholder="email@hotel.de"
              value={manualEmail} onChange={(e) => setManualEmail(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Nome hotel (opzionale)</label>
            <input data-testid="manual-hotel" placeholder="Hotel Beispiel"
              value={manualHotel} onChange={(e) => setManualHotel(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Città (opzionale)</label>
            <input data-testid="manual-city" placeholder="Monaco di Baviera"
              value={manualCity} onChange={(e) => setManualCity(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <button data-testid="send-manual-btn" onClick={sendManual} disabled={sendingManual}
            className="btn-copper w-full px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            {sendingManual ? <Spinner /> : <Send className="w-4 h-4" />}
            {sendingManual ? "Invio in corso..." : `Invia a ${manualEmail.trim() || "email"}`}
          </button>
          <p className="text-[11px] text-gray-500">Usa il template sopra (con variabili) e il CV selezionato. Rispetta il tetto anti-spam giornaliero.</p>
        </div>

        {/* Aggiungi email alla lista */}
        <div className="card p-5 space-y-4 h-fit">
          <h3 className="font-head font-semibold text-gray-200 flex items-center gap-2">
            <Plus className="w-4 h-4 text-amber-400" /> Aggiungi email alla lista
          </h3>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Email</label>
            <input data-testid="add-email" type="email" placeholder="email@hotel.de"
              value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Nome hotel (opzionale)</label>
            <input data-testid="add-hotel" placeholder="Hotel Beispiel"
              value={addHotel} onChange={(e) => setAddHotel(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Città (opzionale)</label>
            <input data-testid="add-city" placeholder="Monaco di Baviera"
              value={addCity} onChange={(e) => setAddCity(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <button data-testid="add-lead-btn" onClick={addLeadToList} disabled={addingLead}
            className="px-4 py-2.5 rounded-lg text-sm border border-white/10 hover:border-amber-500/30 text-gray-200 flex items-center justify-center gap-2 transition-colors">
            {addingLead ? <Spinner /> : <Plus className="w-4 h-4" />}
            {addingLead ? "Aggiunta in corso..." : "Aggiungi alla lista"}
          </button>
        </div>

        {/* Recipients */}
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <select data-testid="region-filter" value={selectedRegion} onChange={(e) => changeRegion(e.target.value)}
                className="input-dark px-3 py-2 text-sm w-auto max-w-[240px]">
                <option value="">Tutte le zone / Länder</option>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button data-testid="select-all-btn" onClick={toggleAll} className="text-sm text-gray-300 flex items-center gap-2">
                {selected.size === leads.length && leads.length > 0 ? <CheckSquare className="w-4 h-4 text-amber-400" /> : <Square className="w-4 h-4" />}
                Seleziona tutti ({selected.size}/{leads.length})
              </button>
            </div>
            <button data-testid="batch-send-btn" onClick={sendBatch} disabled={sending || selected.size === 0}
              className="btn-copper px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              {sending ? <Spinner /> : <Zap className="w-4 h-4" />}
              {sending && progress ? `Invio ${progress.current}/${progress.total}` : `Invia a ${selected.size} hotel`}
            </button>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {loading ? (
              <div className="p-10 flex justify-center"><Spinner className="w-8 h-8" /></div>
            ) : leads.length === 0 ? (
              <EmptyState icon={Send} title={selectedRegion ? `Nessun destinatario in ${selectedRegion}` : "Nessun destinatario disponibile"} hint="Tutti i lead sono già stati contattati o non ci sono email in questa zona." />
            ) : leads.map((l) => (
              <button key={l.id} onClick={() => toggle(l.id)} data-testid={`recipient-${l.id}`}
                className="w-full flex items-center gap-3 p-3 border-b border-white/5 hover:bg-white/[0.03] text-left transition-colors">
                {selected.has(l.id) ? <CheckSquare className="w-4 h-4 text-amber-400 shrink-0" /> : <Square className="w-4 h-4 text-gray-600 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-100 text-sm truncate">{l.hotel_name}</div>
                  <div className="text-xs font-mono text-amber-400/80 truncate">{l.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <Stars count={l.stars} />
                  <div className="text-[11px] text-gray-500">{l.city} {l.region ? `· ${l.region}` : ""}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
